import type { ChipType } from '@sb/cc-design-system';
import dayjs, { Dayjs } from 'dayjs';
import type { IProducts } from 'gcf-components/*';
import { IComponents, IScheduleItemOverrides } from 'gcf-player/shared-utils';
import { isEqual, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ComponentInstanceData,
  EComponentInstanceDataTarget,
  SelectedSection,
} from 'src/components/gcfPreview/GcfPreview.interface';
import { queryClient } from 'src/components/queryProvider';
import {
  DEFAULT_SCREEN_ALL,
  ISO_DATE_FORMAT,
  US_DATE_SLASH_FORMAT,
} from 'src/constants';
import { getUniqueSortedPositions } from 'src/features/locations/schedulePreviewPage/SchedulePreviewPage.helper';
import {
  fetchComponent,
  useGetAllDayparts,
  useGetComponentsWithPagination,
  useModal,
  useParamQuery,
} from 'src/hooks';
import { useGetAllComponentTags } from 'src/hooks/api/componentTags/useGetAllComponentTags';
import { useGetProduct } from 'src/hooks/api/product';
import { useBulkUpdateScheduleOverrides } from 'src/hooks/api/schedules/bulkUpdateScheduleOverrides/useBulkUpdateScheduleOverrides';
import { useGetSchedulePreviewContext } from 'src/hooks/api/schedules/schedulePreview/useGetSchedulePreviewContext';
import { useCreateChangeSet } from 'src/hooks/api/schedules/useCreateChangeSet';
import { useDeleteChangeSet } from 'src/hooks/api/schedules/useDeleteChangeSet';
import { useUpdateChangeSet } from 'src/hooks/api/schedules/useUpdateChangeSet';
import { ComponentTypes, IComponent, IProduct } from 'src/interfaces';
import { IGcfComponentConfig } from 'src/interfaces/Component';
import { lang, queryKeys } from 'src/utils';

import {
  addProductsToLineupComponent,
  applyOptimisticProductOverridePreview,
  buildDeleteScheduleItemOverrideChangeSetPayload,
  buildDraftOverrideDetailInfo,
  buildMatchedComponentsSearchFilters,
  buildScheduleItemOverrideChangeItem,
  buildScheduleItemOverrideChangeSetPayload,
  buildSelectedOverrideDetailInfo,
  cloneComponentWithFreshUids,
  findProductByUid,
  getBaseOverrideFilters,
  getComponentThemeName,
  getImageStyleAndOutageStampBySelectProductKey,
  getLineupFallbackProducts,
  getPreviewContext,
  getPreviewProducts,
  getProductAttributeData,
  getSelectedOverrideProduct,
  handleBuildSelectedSectionInfo,
  hasOverrideDateValidationError,
  hasProductLineupOverride,
  isProductLineupComponent,
  isPromoComponent,
  removeProductFromLineupComponent,
  reorderProductLineupComponent,
  replaceProductInLineupComponent,
  shouldDisableSaveButton,
  updateTargetComponentWithComponentConfig,
} from './MenuBoardModificationPage.helper';
import {
  IOverrideChangeSet,
  IOverrideSelectionInfo,
  IPreviewChangeSetIds,
  ISelectedSectionDetailInfo,
} from './MenuBoardModificationPage.interface';
import { useProductAttributeChangeset } from './modules/productAttributeChangeset/useProductAttributeChangeset';
import { extractVariationsFromConfig } from './modules/productOverrideRightSideBar/ProductOverrideRightSideBar.config';
import { hasProductAttributeValidationError } from './modules/productOverrideRightSideBar/ProductOverrideRightSideBar.helper';
import { useProductOverrideSidebar } from './modules/productOverrideRightSideBar/useProductOverrideSidebar';

type IOverrideFilterComponent = IComponent & {
  layout_type_id?: number;
  component_type_id?: number;
  type?: string;
};

export const useMenuBoardModificationPage = () => {
  const MAX_LOCAL_OVERRIDES = 5;
  const [currentSelectedSection, setCurrentSelectedSection] = useState<
    SelectedSection | undefined
  >();
  const [selectedSectionDetailInfo, setSelectedSectionDetailInfo] = useState<
    ISelectedSectionDetailInfo[]
  >([]);
  const [overrideChangeSets, setOverrideChangeSets] = useState<
    IOverrideChangeSet[]
  >([]);
  const [deletedPreviewChangeSetIds, setDeletedPreviewChangeSetIds] = useState<
    IPreviewChangeSetIds[]
  >([]);
  const [overrideSelectionInfo, setOverrideSelectionInfo] = useState<
    Record<string, IOverrideSelectionInfo>
  >({});
  const [overrideChipDaypartKey, setOverrideChipDaypartKey] = useState<
    string | undefined
  >();
  const [overrideCardIdToScroll, setOverrideCardIdToScroll] = useState<
    string | number | undefined
  >();
  const [activePreviewOverrideId, setActivePreviewOverrideId] = useState<
    string | number | undefined
  >();
  const [overrideFilters, setOverrideFilters] = useState<string[]>([]);
  const [lineupFallbackProducts, setLineupFallbackProducts] =
    useState<IProducts>({});
  const [deletedExistingOverrides, setDeletedExistingOverrides] = useState<
    ISelectedSectionDetailInfo[]
  >([]);
  const [overrideIdToDelete, setOverrideIdToDelete] = useState<
    string | number | undefined
  >();
  const [selectProductKey, setSelectProductKey] = useState('');
  const bulkUpdateScheduleOverridesConfirmationModal = useModal();
  const publicationModal = useModal();
  const { locationCode = '', channelName = '' } = useParams();
  const { getParamsValue, onChangeParams, onChangeMultipleParams } =
    useParamQuery();
  const locationGroupId = getParamsValue('groupID')[0];
  const daypartId = getParamsValue('daypartID')[0] ?? 1;
  const date = getParamsValue('date')[0];
  // useMemo needed for referential stability — selectedDate flows into previewData useMemo deps
  const selectedDate = useMemo(() => dayjs(date), [date]);
  const position = getParamsValue('position')[0] ?? 1;
  const screen = getParamsValue('screen')[0] ?? DEFAULT_SCREEN_ALL;
  const selectedDaypartKey = String(daypartId);
  const showAllowOverrideSections =
    (getParamsValue('showAllowOverrideSections')[0] ?? 'true') === 'true';
  const currentPreviewChangeSetIds = overrideChangeSets
    .filter(
      (changeSet) =>
        overrideSelectionInfo[String(changeSet.changeSetItem.id)]?.previewParams
          .daypartID === selectedDaypartKey &&
        Boolean(changeSet.previewChangeSetId),
    )
    .map((changeSet) => changeSet.previewChangeSetId)
    .concat(
      deletedPreviewChangeSetIds
        .filter(
          (item) =>
            overrideSelectionInfo[item.overrideId]?.previewParams.daypartID ===
            selectedDaypartKey,
        )
        .map((item) => item.changeSetId),
    )
    .filter(Boolean) as string[];

  const createChangeSet = useCreateChangeSet();
  const updateChangeSet = useUpdateChangeSet();
  const deleteChangeSet = useDeleteChangeSet();
  const {
    data: schedulePreviewContext,
    isLoading: isSchedulePreviewContextLoading,
    isFetching: isSchedulePreviewContextFetching,
    error: schedulePreviewContextError,
  } = useGetSchedulePreviewContext(
    locationGroupId,
    locationCode,
    currentPreviewChangeSetIds,
    false,
  );
  const previewProducts = useMemo(
    () => getPreviewProducts(schedulePreviewContext),
    [schedulePreviewContext],
  );
  const previewContext = useMemo(
    () =>
      applyOptimisticProductOverridePreview(
        getPreviewContext(schedulePreviewContext),
        overrideChangeSets,
        lineupFallbackProducts,
        activePreviewOverrideId,
      ),
    [
      schedulePreviewContext,
      overrideChangeSets,
      lineupFallbackProducts,
      activePreviewOverrideId,
    ],
  );

  const { data: daypartOptions, isLoading: isLoadingDayparts } =
    useGetAllDayparts();
  const bulkUpdateScheduleOverrides = useBulkUpdateScheduleOverrides();

  const currentPreviewParams = {
    daypartID: selectedDaypartKey,
    position: String(position),
    screen: String(screen),
  };

  // Chip clicks may target another daypart before the restored query params rerender the page.
  const changeSetDaypartKey = overrideChipDaypartKey || selectedDaypartKey;
  const selectedScheduleItem =
    selectedSectionDetailInfo[selectedSectionDetailInfo.length - 1];
  const selectedScheduleItemId = Number(selectedScheduleItem?.id);
  const selectedDaypartChangeSetItems = useMemo(
    () =>
      overrideChangeSets
        .filter(
          (changeSet) =>
            overrideSelectionInfo[String(changeSet.changeSetItem.id)]
              ?.previewParams.daypartID === selectedDaypartKey,
        )
        .map((changeSet) => changeSet.changeSetItem),
    [overrideChangeSets, overrideSelectionInfo, selectedDaypartKey],
  );
  const changeSetItemsForDaypart = useMemo(
    () =>
      overrideChangeSets
        .filter(
          (changeSet) =>
            overrideSelectionInfo[String(changeSet.changeSetItem.id)]
              ?.previewParams.daypartID === changeSetDaypartKey,
        )
        .map((changeSet) => changeSet.changeSetItem),
    [overrideChangeSets, overrideSelectionInfo, changeSetDaypartKey],
  );

  const overrideChangeSetItems = useMemo(
    () =>
      selectedScheduleItem
        ? changeSetItemsForDaypart.filter(
            (item) => item.scheduleItemId === selectedScheduleItemId,
          )
        : [],
    [changeSetItemsForDaypart, selectedScheduleItem, selectedScheduleItemId],
  );

  const isProductLineupOverride = hasProductLineupOverride(
    selectedScheduleItem,
    overrideChangeSetItems,
  );

  const localOverrideCount = overrideChangeSets.length;
  const hasReachedLocalOverrideLimit =
    localOverrideCount >= MAX_LOCAL_OVERRIDES;
  const overrideValidationChips = overrideChangeSets.map((changeSet) => {
    const item = changeSet.changeSetItem;
    const overrideSelectionItem = overrideSelectionInfo[String(item.id)];
    const chipSelectedSectionDetailInfo =
      overrideSelectionItem?.selectedSectionDetailInfo || [];
    const chipSelectedScheduleItem =
      chipSelectedSectionDetailInfo[chipSelectedSectionDetailInfo.length - 1];
    const isLineupComponent = isProductLineupComponent(item);
    const hasAttributeError =
      isLineupComponent &&
      hasProductAttributeValidationError(item.component?.config);

    // A lineup override clones the base products, so it stays invalid until the
    // user actually edits its products (add / remove / replace / reorder / edit
    // attribute).
    const hasUntouchedLineupProducts =
      isLineupComponent && !item.hasProductEdit;

    const hasError =
      item.isDraft ||
      !item.component ||
      hasOverrideDateValidationError(chipSelectedScheduleItem, item) ||
      hasAttributeError ||
      hasUntouchedLineupProducts;
    const chipType: ChipType = hasError ? 'error' : 'success';

    return {
      id: item.id,
      label: item.isDraft
        ? lang.scheduleComponent.preview.drafting
        : item.component?.name || '',
      chipType,
    };
  });

  const previewStartDate = selectedDate.format(US_DATE_SLASH_FORMAT);
  const previewStartDateFilter = selectedDate.format(ISO_DATE_FORMAT);

  const handleSearchMatchedComponents = useCallback((value: string) => {
    setOverrideFilters((currentFilters) => [
      ...getBaseOverrideFilters(currentFilters),
      ...buildMatchedComponentsSearchFilters(value),
    ]);
  }, []);

  const selectedDaypartData = daypartOptions?.find(
    (daypart) => daypart.id === Number(daypartId),
  );
  const {
    data: matchedComponentsData,
    isLoading: isMatchedComponentsLoading,
    isFetching: isMatchedComponentsFetching,
    hasNextPage: hasNextMatchedComponentsPage,
    fetchNextPage: fetchNextMatchedComponentsPage,
    isFetchingNextPage: isFetchingNextMatchedComponentsPage,
  } = useGetComponentsWithPagination(overrideFilters, {
    enabled: overrideFilters.length > 0,
  });
  const { data: allComponentTags = [] } = useGetAllComponentTags();

  const matchedComponents = useMemo(
    () =>
      uniqBy(
        matchedComponentsData?.pages?.flatMap((page) => page?.data || []) || [],
        (component) => component.id,
      ).map((component) => ({
        ...component,
        componentTags: component.componentTags?.length
          ? component.componentTags
          : allComponentTags.filter((tag) =>
              (component.component_tag_id || []).includes(tag.id),
            ),
      })),
    [allComponentTags, matchedComponentsData],
  );

  const isCreatingOverride = createChangeSet.isLoading;
  const isRefreshingPreview = isSchedulePreviewContextFetching;
  const isAddOverrideDisabled = isCreatingOverride || isRefreshingPreview;

  const { productImageStyleAndOutageStamp, handleProductAttributeChange } =
    useProductAttributeChangeset({
      selectedScheduleItem,
      overrideChangeSets,
      setOverrideChangeSets,
      updateChangeSet,
      createChangeSet,
      locationGroupId,
      locationCode,
      changeSetDaypartKey,
    });

  const getOverrideFilters = (
    scheduleItem: ISelectedSectionDetailInfo | undefined,
  ) => {
    if (!scheduleItem?.componentId || !scheduleItem.component) {
      return [];
    }

    const selectedOverrideComponent =
      scheduleItem.component as unknown as IOverrideFilterComponent;
    const selectedComponentTypeName =
      selectedOverrideComponent.component_type?.name ||
      selectedOverrideComponent.type;
    const isPromoComponent =
      selectedComponentTypeName === ComponentTypes.PROMOTIONAL_SPACE ||
      selectedComponentTypeName === ComponentTypes.PROMOTIONAL_COMPONENT;

    if (!isPromoComponent) {
      return [];
    }

    const scheduleStartDate = dayjs(scheduleItem.startDate);
    const now = dayjs().add(1, 'day');
    const fromDate = now.isBefore(scheduleStartDate) ? scheduleStartDate : now;
    const formattedFromDate = fromDate.format(ISO_DATE_FORMAT);

    return [
      `end_date.from=${formattedFromDate}`,
      `width=${selectedOverrideComponent.width}`,
      `height=${selectedOverrideComponent.height}`,
      `layout_type_id=${selectedOverrideComponent.layout_type_id}`,
      `id.notlist=${scheduleItem.componentId}`,
      `component_type_id.list=${selectedOverrideComponent.component_type_id}`,
      'usable_as_override=true',
      `start_date.to=${previewStartDateFilter}`,
      'start_date.null=true',
      'status=ready',
      'end_date.null=true',
      'sort=-modified_at',
    ];
  };

  const handleUnselectSection = useCallback(() => {
    setCurrentSelectedSection((prev) =>
      prev === undefined ? prev : undefined,
    );
    setSelectedSectionDetailInfo((prev) => (prev.length ? [] : prev));
    setOverrideChipDaypartKey((prev) =>
      prev === undefined ? prev : undefined,
    );
    setActivePreviewOverrideId((prev) =>
      prev === undefined ? prev : undefined,
    );
    setOverrideFilters((prev) => (prev.length ? [] : prev));
    setSelectProductKey('');
  }, []);
  const handleChangeSelectedDate = useCallback(
    (newDate: Dayjs) => {
      const nextAvailablePositions = getUniqueSortedPositions(
        newDate,
        previewContext?.location?.channels,
        selectedDaypartData?.name,
      );
      const nextPosition = nextAvailablePositions.includes(Number(position))
        ? String(position)
        : '1';

      onChangeMultipleParams({
        date: newDate.format(ISO_DATE_FORMAT),
        position: nextPosition,
      });
      handleUnselectSection();
    },
    [
      handleUnselectSection,
      onChangeMultipleParams,
      position,
      previewContext?.location?.channels,
      selectedDaypartData?.name,
    ],
  );
  const handleChangeSelectedDaypart = (daypart: string) => {
    onChangeParams('daypartID', daypart);
    handleUnselectSection();
  };

  const handleChangeToggleValue = (value: boolean) => {
    onChangeParams('showAllowOverrideSections', String(value));
    handleUnselectSection();
  };
  const handleChangeSelectedScreen = (screenValue: string) => {
    onChangeParams('screen', screenValue);
  };
  const createValidatedOverrideChangeSet = async (
    overrideItem: ISelectedSectionDetailInfo,
  ) => {
    const overrideChangeSet = overrideChangeSets.find(
      (changeSet) =>
        String(changeSet.changeSetItem.id) === String(overrideItem.id),
    );
    // Use the fresh override item config first. State updates are async, so
    // reading overrideChangeSets immediately after replace can still return
    // the previous component config from the prior render.
    const latestComponentConfig =
      overrideItem.component?.config ??
      overrideChangeSet?.changeSetItem.component?.config;
    const productLineupConfig =
      isProductLineupComponent(overrideItem) && latestComponentConfig
        ? latestComponentConfig
        : undefined;
    const scheduleItemOverrideProductVariations = productLineupConfig
      ? extractVariationsFromConfig(productLineupConfig)
      : undefined;
    const changeItem = buildScheduleItemOverrideChangeItem({
      override: {
        id: overrideItem.id as string,
        endDate: overrideItem.endDate,
        startDate: overrideItem.startDate,
        componentId: overrideItem.componentId!,
        scheduleItemId: overrideItem.scheduleItemId!,
        ...(productLineupConfig && { componentConfig: productLineupConfig }),
        ...(scheduleItemOverrideProductVariations && {
          scheduleItemOverrideProductVariations,
        }),
      },
    });

    if (overrideChangeSet?.previewChangeSetId) {
      await updateChangeSet.mutateAsync({
        changeSetId: Number(overrideChangeSet.previewChangeSetId),
        payload: { changes: [changeItem] },
      });
    } else {
      const payload = buildScheduleItemOverrideChangeSetPayload({
        locationGroupId: Number(locationGroupId),
        locationCode,
        daypartId: Number(changeSetDaypartKey),
        override: {
          id: overrideItem.id as string,
          endDate: overrideItem.endDate,
          startDate: overrideItem.startDate,
          componentId: overrideItem.componentId!,
          scheduleItemId: overrideItem.scheduleItemId!,
          ...(productLineupConfig && { componentConfig: productLineupConfig }),
          ...(scheduleItemOverrideProductVariations && {
            scheduleItemOverrideProductVariations,
          }),
        },
      });

      const response = await createChangeSet.mutateAsync(payload);

      if (!response?.id) {
        return;
      }

      setOverrideChangeSets((currentOverrideChangeSets) =>
        currentOverrideChangeSets.map((changeSet) =>
          String(changeSet.changeSetItem.id) === String(overrideItem.id)
            ? {
                ...changeSet,
                previewChangeSetId: String(response.id),
              }
            : changeSet,
        ),
      );
    }
  };

  const updateOverrideItemState = (
    overrideId: string | number,
    nextItem: ISelectedSectionDetailInfo,
  ) => {
    setOverrideChangeSets((currentOverrideChangeSets) => {
      if (
        !currentOverrideChangeSets.some(
          (changeSet) =>
            String(changeSet.changeSetItem.id) === String(overrideId),
        )
      ) {
        return currentOverrideChangeSets;
      }

      return currentOverrideChangeSets.map((changeSet) =>
        String(changeSet.changeSetItem.id) === String(overrideId)
          ? {
              ...changeSet,
              // Merge instead of replace — preserves optimistic config updates
              // from handleProductAttributeChange that may still be in-flight
              changeSetItem: {
                ...changeSet.changeSetItem,
                ...nextItem,
              },
            }
          : changeSet,
      );
    });
  };
  const getOverrideChangeSetById = (overrideId: string | number) =>
    overrideChangeSets.find(
      (changeSet) => String(changeSet.changeSetItem.id) === String(overrideId),
    );
  const removeLocalOverride = (overrideId: string | number) => {
    setOverrideChangeSets((currentOverrideChangeSets) =>
      currentOverrideChangeSets.filter(
        (changeSet) =>
          String(changeSet.changeSetItem.id) !== String(overrideId),
      ),
    );

    setOverrideSelectionInfo((currentOverrideSelectionInfo) => {
      const overrideKey = String(overrideId);

      if (!currentOverrideSelectionInfo[overrideKey]) {
        return currentOverrideSelectionInfo;
      }

      const updatedOverrideSelectionInfo = {
        ...currentOverrideSelectionInfo,
      };
      delete updatedOverrideSelectionInfo[overrideKey];

      return updatedOverrideSelectionInfo;
    });
  };

  const handleAddOverride = () => {
    if (
      hasReachedLocalOverrideLimit ||
      !selectedScheduleItem?.componentId ||
      !selectedScheduleItem.component
    ) {
      return;
    }

    if (isPromoComponent(selectedScheduleItem)) {
      const overrideFiltersForSection =
        getOverrideFilters(selectedScheduleItem);

      if (!overrideFiltersForSection.length) {
        setOverrideFilters([]);

        return;
      }

      const newDraftOverride = buildDraftOverrideDetailInfo(
        selectedScheduleItem,
        selectedDate.format(US_DATE_SLASH_FORMAT),
      );

      setOverrideChangeSets((currentOverrideChangeSets) => [
        {
          previewChangeSetId: '',
          changeSetItem: newDraftOverride,
        },
        ...currentOverrideChangeSets,
      ]);

      setOverrideSelectionInfo((prev) => ({
        ...prev,
        [String(newDraftOverride.id)]: {
          currentSelectedSection,
          selectedSectionDetailInfo,
          overrideFilters: overrideFiltersForSection,
          previewParams: currentPreviewParams,
        },
      }));
      setOverrideFilters(overrideFiltersForSection);
      setActivePreviewOverrideId(newDraftOverride.id);
    } else if (isProductLineupComponent(selectedScheduleItem)) {
      const newDraftOverride = buildDraftOverrideDetailInfo(
        selectedScheduleItem,
        selectedDate.format(US_DATE_SLASH_FORMAT),
        {
          isDraft: false,
          componentId: selectedScheduleItem.componentId,
          component: selectedScheduleItem.component
            ? cloneComponentWithFreshUids(selectedScheduleItem.component)
            : undefined,
        },
      );

      setOverrideChangeSets((currentOverrideChangeSets) => [
        {
          previewChangeSetId: '',
          changeSetItem: newDraftOverride,
        },
        ...currentOverrideChangeSets,
      ]);

      setOverrideSelectionInfo((prev) => ({
        ...prev,
        [String(newDraftOverride.id)]: {
          currentSelectedSection,
          selectedSectionDetailInfo,
          overrideFilters: [],
          previewParams: currentPreviewParams,
        },
      }));
      setActivePreviewOverrideId(newDraftOverride.id);
    }
  };
  const handleDeleteOverride = (overrideId: string | number) => {
    const overrideChangeSetToRemove = getOverrideChangeSetById(overrideId);

    if (overrideChangeSetToRemove?.changeSetItem.isDraft) {
      removeLocalOverride(overrideId);

      return;
    }

    setOverrideIdToDelete(overrideId);
  };
  const handleCloseOverrideDeleteModal = () => {
    setOverrideIdToDelete(undefined);
  };
  const handleConfirmDeleteOverride = async () => {
    if (overrideIdToDelete === undefined) {
      return;
    }

    const overrideChangeSetToRemove =
      getOverrideChangeSetById(overrideIdToDelete);
    removeLocalOverride(overrideIdToDelete);
    setOverrideIdToDelete(undefined);

    if (!overrideChangeSetToRemove?.previewChangeSetId) {
      return;
    }

    await deleteChangeSet.mutateAsync(
      overrideChangeSetToRemove.previewChangeSetId,
    );
  };

  const handleSelectPromo = async (component: IComponent) => {
    if (!selectedScheduleItem) {
      return;
    }

    const matchingOverrideItem = overrideChangeSetItems.find(
      (item) =>
        item.scheduleItemId === selectedScheduleItemId &&
        item.type === EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE,
    );
    const matchingDraftOverride = overrideChangeSetItems.find(
      (item) =>
        item.scheduleItemId === selectedScheduleItemId && Boolean(item.isDraft),
    );
    const targetOverrideItem = matchingDraftOverride || matchingOverrideItem;
    if (!targetOverrideItem) return;

    const overrideDetail = buildSelectedOverrideDetailInfo(
      selectedScheduleItem,
      component as any,
      targetOverrideItem.startDate || selectedDate.format(US_DATE_SLASH_FORMAT),
      targetOverrideItem.id,
      targetOverrideItem.endDate,
    );

    setOverrideChangeSets((currentOverrideChangeSets) =>
      currentOverrideChangeSets.map((changeSet) =>
        String(changeSet.changeSetItem.id) === String(targetOverrideItem.id)
          ? {
              ...changeSet,
              changeSetItem: overrideDetail,
            }
          : changeSet,
      ),
    );

    if (!hasOverrideDateValidationError(selectedScheduleItem, overrideDetail)) {
      await createValidatedOverrideChangeSet(overrideDetail);
    }
  };
  const handleChangeOverrideStartDate = async (
    overrideId: string | number,
    newDate: Dayjs | null,
  ) => {
    if (!newDate) {
      return;
    }

    const overrideItem = overrideChangeSetItems.find(
      (item) => String(item.id) === String(overrideId),
    );

    if (!overrideItem) {
      return;
    }

    const nextOverrideItem = {
      ...overrideItem,
      startDate: newDate.format(US_DATE_SLASH_FORMAT),
    };

    updateOverrideItemState(overrideId, nextOverrideItem);

    if (
      !hasOverrideDateValidationError(selectedScheduleItem, nextOverrideItem)
    ) {
      await createValidatedOverrideChangeSet(nextOverrideItem);
    }
  };
  const handleChangeOverrideEndDate = async (
    overrideId: string | number,
    newDate: Dayjs | null,
  ) => {
    const overrideItem = overrideChangeSetItems.find(
      (item) => String(item.id) === String(overrideId),
    );

    if (!overrideItem) {
      return;
    }

    const nextOverrideItem = {
      ...overrideItem,
      endDate: newDate ? newDate.format(US_DATE_SLASH_FORMAT) : null,
    };

    updateOverrideItemState(overrideId, nextOverrideItem);

    if (
      !hasOverrideDateValidationError(selectedScheduleItem, nextOverrideItem)
    ) {
      await createValidatedOverrideChangeSet(nextOverrideItem);
    }
  };

  const handleClickSection = async (
    event: ComponentInstanceData,
    selectedSection: SelectedSection | undefined,
    components: IComponents,
    overrides: IScheduleItemOverrides,
  ) => {
    onChangeParams('showAllowOverrideSections', String(false));

    if (isEqual(selectedSection, currentSelectedSection)) {
      handleUnselectSection();
    } else {
      let componentConfigWithNewStructure;

      if (
        isProductLineupComponent({
          component: components?.[event.ref?.componentId],
        })
      ) {
        const component = await queryClient.fetchQuery({
          queryKey: queryKeys.components.details([
            event.ref?.componentId,
            `enabledTransform=true`,
          ]),
          queryFn: () => fetchComponent(event.ref?.componentId, true),
        });

        componentConfigWithNewStructure = component.config;
      }

      const formattedComponents = updateTargetComponentWithComponentConfig(
        components,
        event.ref?.componentId,
        componentConfigWithNewStructure,
      );

      const nextSelectedSectionDetailInfo = handleBuildSelectedSectionInfo(
        event,
        formattedComponents,
        overrides,
        date,
      );
      const nextSelectedScheduleItem =
        nextSelectedSectionDetailInfo[nextSelectedSectionDetailInfo.length - 1];
      const existingOverrideItemsForSelectedSection = nextSelectedScheduleItem
        ? selectedDaypartChangeSetItems.filter(
            (item) =>
              item.scheduleItemId === Number(nextSelectedScheduleItem.id),
          )
        : [];
      const hasOverrideForSelectedSection =
        existingOverrideItemsForSelectedSection.length > 0;
      const overrideFiltersForSection = hasOverrideForSelectedSection
        ? getOverrideFilters(nextSelectedScheduleItem)
        : [];

      setOverrideChipDaypartKey(undefined);
      setCurrentSelectedSection(selectedSection);
      setSelectedSectionDetailInfo(nextSelectedSectionDetailInfo);
      setOverrideFilters(overrideFiltersForSection);

      if (existingOverrideItemsForSelectedSection.length) {
        setOverrideSelectionInfo((prev) => {
          const nextOverrideSelectionInfo = {
            currentSelectedSection: selectedSection,
            selectedSectionDetailInfo: nextSelectedSectionDetailInfo,
            overrideFilters: overrideFiltersForSection,
            previewParams: currentPreviewParams,
          };

          return {
            ...prev,
            ...Object.fromEntries(
              existingOverrideItemsForSelectedSection.map((item) => [
                String(item.id),
                nextOverrideSelectionInfo,
              ]),
            ),
          };
        });
      }
    }
  };

  const handleOverrideValidationChipClick = (overrideId: string | number) => {
    const overrideSelectionItem = overrideSelectionInfo[String(overrideId)];
    const overrideItem = overrideChangeSets.find(
      (changeSet) => String(changeSet.changeSetItem.id) === String(overrideId),
    )?.changeSetItem;

    if (overrideSelectionItem) {
      const { daypartID, position, screen } =
        overrideSelectionItem.previewParams;
      const overrideStartDate = dayjs(overrideItem?.startDate);
      const restoredDate = overrideStartDate.isValid()
        ? overrideStartDate.format(ISO_DATE_FORMAT)
        : dayjs().format(ISO_DATE_FORMAT);

      setOverrideChipDaypartKey(daypartID);
      onChangeMultipleParams({
        daypartID,
        position,
        screen,
        date: restoredDate,
        showAllowOverrideSections: String(false),
      });
      setCurrentSelectedSection(overrideSelectionItem.currentSelectedSection);
      setSelectedSectionDetailInfo(
        overrideSelectionItem.selectedSectionDetailInfo,
      );
      setOverrideFilters(overrideSelectionItem.overrideFilters);
    }

    setOverrideCardIdToScroll(overrideId);
    setActivePreviewOverrideId(overrideId);
  };
  const handleOverrideCardScrollComplete = () => {
    setOverrideCardIdToScroll(undefined);
  };

  const handleSave = async () => {
    bulkUpdateScheduleOverridesConfirmationModal.onOpenModal(async () => {
      // UI keeps overrides newest-first (prepend), but backend commits in request order.
      // Reverse so change sets are sent oldest->newest, making newest apply last.
      const createdChangeSetIds = [...overrideChangeSets]
        .reverse()
        .map((changeSet) => Number(changeSet.previewChangeSetId));
      const deletedChangeSetIds = deletedPreviewChangeSetIds.map((item) =>
        Number(item.changeSetId),
      );

      await bulkUpdateScheduleOverrides.mutateAsync([
        ...createdChangeSetIds,
        ...deletedChangeSetIds,
      ]);

      bulkUpdateScheduleOverridesConfirmationModal.onCloseModal();
      publicationModal.onOpenModal();
    });
  };

  const handleDeleteExistingOverride = async (
    overrideItem: ISelectedSectionDetailInfo,
  ) => {
    const payload = buildDeleteScheduleItemOverrideChangeSetPayload({
      locationGroupId: Number(locationGroupId),
      locationCode,
      daypartId: Number(changeSetDaypartKey),
      override: {
        scheduleOverrideId: overrideItem.scheduleOverrideId ?? overrideItem.id,
        scheduleItemId: overrideItem.scheduleItemId ?? '',
      },
    });

    const response = await createChangeSet.mutateAsync(payload);

    if (!response?.id) {
      return;
    }

    setDeletedPreviewChangeSetIds((prev) => [
      ...prev,
      {
        changeSetId: String(response.id),
        overrideId: String(overrideItem.id),
      },
    ]);

    setDeletedExistingOverrides((prev) => [...prev, overrideItem]);
    setSelectedSectionDetailInfo((prev) =>
      prev.filter(
        (item) =>
          String(item.scheduleOverrideId ?? item.id) !==
          String(overrideItem.scheduleOverrideId ?? overrideItem.id),
      ),
    );
    setOverrideSelectionInfo((prev) => ({
      ...prev,
      [String(overrideItem.id)]: {
        currentSelectedSection,
        selectedSectionDetailInfo,
        overrideFilters,
        previewParams: currentPreviewParams,
      },
    }));
  };

  const cacheLineupFallbackProducts = useCallback(
    (products: IProduct[]) => {
      const selectedMissingProducts = products.filter(
        (product) =>
          product.product_code && !previewProducts?.[product.product_code],
      );

      if (!selectedMissingProducts.length) {
        return;
      }

      setLineupFallbackProducts((currentLineupFallbackProducts) => {
        const uncachedProducts = selectedMissingProducts.filter(
          (product) => !currentLineupFallbackProducts[product.product_code],
        );

        if (!uncachedProducts.length) {
          return currentLineupFallbackProducts;
        }

        const nextLineupFallbackProducts =
          getLineupFallbackProducts(uncachedProducts);

        return {
          ...currentLineupFallbackProducts,
          ...nextLineupFallbackProducts,
        };
      });
    },
    [previewProducts],
  );

  const handleAddAccordionProducts = async (
    overrideId: string | number,
    groupKey: string,
    products: IProduct[],
  ) => {
    const overrideItem = overrideChangeSetItems.find(
      (item) => String(item.id) === String(overrideId),
    );

    if (!overrideItem?.component || !overrideItem.componentId) {
      return;
    }

    const { component: nextComponent, addedProductUids } =
      addProductsToLineupComponent(
        overrideItem.component,
        groupKey,
        products,
        getComponentThemeName(selectedScheduleItem?.component),
      );

    if (nextComponent === overrideItem.component) {
      return;
    }

    cacheLineupFallbackProducts(products);

    const nextOverrideItem = {
      ...overrideItem,
      component: nextComponent,
      componentId: nextComponent.id,
      isDraft: false,
      hasProductEdit: true,
    };
    const applyOverrideUpdate = (item: ISelectedSectionDetailInfo) =>
      String(item.id) === String(overrideId) ? nextOverrideItem : item;

    updateOverrideItemState(overrideId, nextOverrideItem);

    setSelectProductKey(addedProductUids[0] ?? '');

    setOverrideSelectionInfo((prev) => {
      const currentOverrideSelection = prev[String(overrideId)];

      if (!currentOverrideSelection) {
        return prev;
      }

      return {
        ...prev,
        [String(overrideId)]: {
          ...currentOverrideSelection,
          selectedSectionDetailInfo:
            currentOverrideSelection.selectedSectionDetailInfo.map(
              applyOverrideUpdate,
            ),
        },
      };
    });

    if (
      !hasOverrideDateValidationError(selectedScheduleItem, nextOverrideItem)
    ) {
      await createValidatedOverrideChangeSet(nextOverrideItem);
    }
  };

  const handleReplaceAccordionProduct = async (
    overrideId: string | number,
    productKey: string,
    product: IProduct,
  ) => {
    const overrideItem = overrideChangeSetItems.find(
      (item) => String(item.id) === String(overrideId),
    );

    if (!overrideItem?.component || !overrideItem.componentId) {
      return;
    }

    const imageAttributes = getImageStyleAndOutageStampBySelectProductKey(
      productKey,
      overrideItem.component.config,
      productImageStyleAndOutageStamp,
    );

    const { component: nextComponent, replacementProductUid } =
      replaceProductInLineupComponent(
        overrideItem.component,
        productKey,
        product,
        imageAttributes,
        getComponentThemeName(selectedScheduleItem?.component),
      );

    if (nextComponent === overrideItem.component) {
      return;
    }
    cacheLineupFallbackProducts([product]);

    const nextOverrideItem = {
      ...overrideItem,
      component: nextComponent,
      componentId: nextComponent.id,
      isDraft: false,
      hasProductEdit: true,
    };
    const applyOverrideUpdate = (item: ISelectedSectionDetailInfo) =>
      String(item.id) === String(overrideId)
        ? {
            ...item,
            component: nextComponent,
            componentId: nextComponent.id,
            isDraft: false,
          }
        : item;

    updateOverrideItemState(overrideId, nextOverrideItem);
    setSelectProductKey(replacementProductUid ?? '');
    setOverrideSelectionInfo((prev) => {
      const currentOverrideSelection = prev[String(overrideId)];

      if (!currentOverrideSelection) {
        return prev;
      }

      return {
        ...prev,
        [String(overrideId)]: {
          ...currentOverrideSelection,
          selectedSectionDetailInfo:
            currentOverrideSelection.selectedSectionDetailInfo.map(
              applyOverrideUpdate,
            ),
        },
      };
    });
    if (
      !hasOverrideDateValidationError(selectedScheduleItem, nextOverrideItem)
    ) {
      await createValidatedOverrideChangeSet(nextOverrideItem);
    }
  };

  const handleRemoveAccordionProduct = async (
    overrideId: string | number,
    productKey: string,
  ) => {
    const overrideItem = overrideChangeSetItems.find(
      (item) => String(item.id) === String(overrideId),
    );

    if (!overrideItem?.component || !overrideItem.componentId) {
      return;
    }

    const nextComponent = removeProductFromLineupComponent(
      overrideItem.component,
      productKey,
    );

    if (nextComponent === overrideItem.component) {
      return;
    }

    // If the removed product was selected, clear the selection
    if (productKey === selectProductKey) {
      setSelectProductKey('');
    }

    const nextOverrideItem = {
      ...overrideItem,
      component: nextComponent,
      componentId: nextComponent.id,
      isDraft: false,
      hasProductEdit: true,
    };
    const applyOverrideUpdate = (item: ISelectedSectionDetailInfo) =>
      String(item.id) === String(overrideId) ? nextOverrideItem : item;

    updateOverrideItemState(overrideId, nextOverrideItem);
    setSelectedSectionDetailInfo((prev) => prev.map(applyOverrideUpdate));
    setOverrideSelectionInfo((prev) => {
      const currentOverrideSelection = prev[String(overrideId)];

      if (!currentOverrideSelection) {
        return prev;
      }

      return {
        ...prev,
        [String(overrideId)]: {
          ...currentOverrideSelection,
          selectedSectionDetailInfo:
            currentOverrideSelection.selectedSectionDetailInfo.map(
              applyOverrideUpdate,
            ),
        },
      };
    });

    if (
      !hasOverrideDateValidationError(selectedScheduleItem, nextOverrideItem)
    ) {
      await createValidatedOverrideChangeSet(nextOverrideItem);
    }
  };

  const onSelectAccordionProduct = (productKey: string) => {
    setSelectProductKey(productKey);
  };

  const baseComponentConfig = useMemo(() => {
    const fallback = (overrideChangeSetItems.find((item) => !item.isDraft)
      ?.component?.config ?? selectedScheduleItem?.component?.config) as
      | IGcfComponentConfig
      | undefined;

    if (!selectProductKey) return fallback;

    const matchingOverride = overrideChangeSetItems.find(
      (item) =>
        !item.isDraft &&
        findProductByUid(
          item.component?.config as IGcfComponentConfig | undefined,
          selectProductKey,
        ),
    );

    return (matchingOverride?.component?.config ?? fallback) as
      | IGcfComponentConfig
      | undefined;
  }, [selectProductKey, overrideChangeSetItems, selectedScheduleItem]);

  const { productCode = '' } = getSelectedOverrideProduct(
    baseComponentConfig,
    selectProductKey,
  );

  // TODO: it may be able to be removed as product info is from product modal now
  const {
    data: productData,
    isLoading: isProductLoading,
    isFetching: isProductFetching,
  } = useGetProduct(productCode, [
    'products',
    'productVariationSizes',
    'productVariationFlavors',
    'assets',
    'themes',
  ]);

  const themeName = getComponentThemeName(selectedScheduleItem?.component);

  const { productConfig, attributeSizes, productVariationsAssets } = useMemo(
    () =>
      getProductAttributeData(
        baseComponentConfig,
        selectProductKey,
        productData,
        themeName,
      ),
    [baseComponentConfig, selectProductKey, productData, themeName],
  );

  const mainProductAssetName =
    productData?.asset_name?.[0] ?? productData?.assets?.[0]?.name;

  const {
    localAttributes,
    setLocalAttributes,
    defaultSize,
    defaultFlavor,
    showImage,
    defaultImage,
  } = useProductOverrideSidebar({
    attributeSizes,
    productConfig,
    mainProductAssetName,
    selectProductKey,
    isProductFetching,
    productCode,
    productVariationsAssets,
  });

  // Reset invalid date to today when URL param is missing or in the past
  useEffect(() => {
    const dateObj = dayjs(date);

    if (!dateObj.isValid() || dateObj.isBefore(dayjs(), 'day')) {
      handleChangeSelectedDate(dayjs());
    }
  }, [date, handleChangeSelectedDate]);

  const onReorderAccordionProduct = async (
    overrideId: string | number,
    accordionKey: string,
    oldIndex: number,
    newIndex: number,
  ) => {
    const targetOverrideItem = overrideChangeSetItems.find(
      (item) => String(item.id) === String(overrideId),
    );

    if (!targetOverrideItem?.component) {
      return;
    }

    const nextComponent = reorderProductLineupComponent(
      targetOverrideItem.component,
      accordionKey,
      oldIndex,
      newIndex,
    );

    if (!nextComponent || nextComponent === targetOverrideItem.component) {
      return;
    }

    const nextOverrideItem = {
      ...targetOverrideItem,
      component: nextComponent,
      hasProductEdit: true,
    };
    const applyOverrideUpdate = (item: ISelectedSectionDetailInfo) =>
      String(item.id) === String(targetOverrideItem.id)
        ? {
            ...item,
            component: nextComponent,
          }
        : item;

    updateOverrideItemState(targetOverrideItem.id, nextOverrideItem);
    setOverrideSelectionInfo((prev) => {
      const currentOverrideSelection = prev[String(targetOverrideItem.id)];

      if (!currentOverrideSelection) {
        return prev;
      }

      return {
        ...prev,
        [String(targetOverrideItem.id)]: {
          ...currentOverrideSelection,
          selectedSectionDetailInfo:
            currentOverrideSelection.selectedSectionDetailInfo.map(
              applyOverrideUpdate,
            ),
        },
      };
    });
    if (
      !hasOverrideDateValidationError(selectedScheduleItem, nextOverrideItem)
    ) {
      await createValidatedOverrideChangeSet(nextOverrideItem);
    }
  };

  return {
    showAllowOverrideSections,
    previewContext,
    isSchedulePreviewContextLoading,
    isSchedulePreviewContextFetching,
    schedulePreviewContextError,
    isLoadingDayparts,
    selectedDaypartData,
    selectedDate,
    previewStartDate,
    selectedPosition: position,
    selectedScreen: screen,
    daypartOptions,
    selectedDaypart: daypartId,
    locationCode,
    locationGroupId,
    channelName,
    currentSelectedSection,
    selectedSectionDetailInfo,
    selectedScheduleItem,
    overrideChangeSets,
    overrideChangeSetItems,
    hasReachedLocalOverrideLimit,
    isAddOverrideDisabled,
    overrideCardIdToScroll,
    overrideValidationChips,
    overrideIdToDelete,
    matchedComponents,
    isMatchedComponentsLoading,
    isMatchedComponentsFetching,
    hasNextMatchedComponentsPage,
    isFetchingNextMatchedComponentsPage,
    bulkUpdateScheduleOverridesConfirmationModal,
    publicationModal,
    bulkUpdateScheduleOverrides,
    isSaveButtonDisabled: shouldDisableSaveButton(
      overrideValidationChips,
      deletedExistingOverrides,
      overrideChangeSets,
      deletedPreviewChangeSetIds,
    ),
    selectProductKey,
    isProductLineupOverride,
    isProductLoading,
    localAttributes,
    setLocalAttributes,
    defaultSize,
    defaultFlavor,
    showImage,
    defaultImage,
    productCode,
    productVariationsAssets,
    lineupFallbackProducts,
    fetchNextMatchedComponentsPage,
    handleSearchMatchedComponents,
    onChangeParams,
    handleChangeSelectedDate,
    handleChangeSelectedDaypart,
    handleChangeSelectedScreen,
    handleChangeToggleValue,
    handleChangeOverrideStartDate,
    handleChangeOverrideEndDate,
    handleDeleteOverride,
    handleDeleteExistingOverride,
    handleCloseOverrideDeleteModal,
    handleConfirmDeleteOverride,
    handleUnselectSection,
    handleClickSection,
    handleOverrideValidationChipClick,
    handleOverrideCardScrollComplete,
    handleAddOverride,
    handleSelectPromo,
    handleSave,
    setCurrentSelectedSection,
    onSelectAccordionProduct,
    onReorderAccordionProduct,
    onAddAccordionProducts: handleAddAccordionProducts,
    onReplaceAccordionProduct: handleReplaceAccordionProduct,
    handleProductAttributeChange,
    onRemoveAccordionProduct: handleRemoveAccordionProduct,
  };
};
